// CANVAS-CORE + LIVE-EVENTS: the Live session's channel in the browser
// (`GET /v1/companies/{co}/live/canvas/events`, server-sent events). Frames:
//   ready             {cursor, session_id}: the stream is open
//   run.step          LiveRunStep: merged into the steps strip (deduplicated by run and seq)
//   task.changed      ChangePing: the screens that show the subject refetch (live/change-pings)
//   helper.commitment ChangePing: likewise
//   canvas.op         LiveCanvasOp: applied to the cached canvas (a show carries only blocks the
//                     viewer may see); a `focus_view` is also kept as the page's `focus`
//   resync            frames may have been lost: the canvas is refetched
//   end               {reason}: revoked / unauthenticated stops; anything else reconnects
// Every frame's `id` is the resume cursor (EventSource sends it back as Last-Event-ID). While
// the stream is not open the canvas query polls (`useLiveCanvas`), the same fallback as the
// change pings. Against the in-memory fake there is nothing to stream: the hook does nothing.
import {
  type QueryClient,
  type UseQueryResult,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import type { Api } from "../../api/client";
import { type ChangePing, type ChangeSource, keysForPings } from "../../live/change-pings";
import {
  applyFrame,
  type CanvasState,
  canvasKeys,
  type LiveCanvasOp,
  type LiveRunStep,
  mergeSteps,
} from "./canvas-state";

/** How often the canvas is re-read while the stream is not open. */
export const CANVAS_POLL_MS = 5_000;

/**
 * CANVAS-BLOCKS: the latest `focus_view` frame (the agent asks the Live page to switch view).
 * Kept in the query cache beside the steps; `id` is the frame's, so each one acts once.
 */
export type LiveFocus = {
  id: string;
  view: "workspace" | "browser" | "computer";
  session: string | null;
  tab: string | null;
  reason: string | null;
  at: string;
};

/** The cache key of a Live session's latest `focus_view`. */
export const liveFocusKey = (companyId: string, sessionId: string) =>
  ["company", companyId, "live-canvas", sessionId, "focus"] as const;

/** A `focus_view` frame as the page's focus; null for any other op. */
export function focusOfFrame(frame: LiveCanvasOp): LiveFocus | null {
  const op = frame.op;
  if (op.op !== "focus_view") return null;
  return {
    id: frame.id,
    view: op.view,
    session: op.session ?? null,
    tab: op.tab ?? null,
    reason: op.reason ?? null,
    at: frame.at,
  };
}

/**
 * LIVE-DESKTOP: the latest canvas ops in arrival order (at most `DESK_OPS_KEPT`), so the desk
 * can react to each once: a show places or restores its windows, an arrange lays them out, a
 * highlight fronts one. The state fold alone cannot say an op happened twice.
 */
export const liveOpsKey = (companyId: string, sessionId: string) =>
  ["company", companyId, "live-canvas", sessionId, "ops"] as const;
export const DESK_OPS_KEPT = 20;

/** The channel URL for one Live session on the API at `baseUrl` (`/` = same origin). */
export function liveCanvasEventsUrl(
  baseUrl: string,
  companyId: string,
  sessionId: string,
  lastEventId?: string,
): string {
  const q = new URLSearchParams({ session_id: sessionId });
  if (lastEventId) q.set("last_event_id", lastEventId);
  return `${baseUrl.replace(/\/$/, "")}/v1/companies/${encodeURIComponent(companyId)}/live/canvas/events?${q}`;
}

/** This tab's Live session id (kept in sessionStorage; a fresh one where storage is blocked). */
export function liveSessionId(companyId: string): string {
  const key = `cos.live-canvas.session.${companyId}`;
  try {
    const kept = sessionStorage.getItem(key);
    if (kept) return kept;
  } catch {}
  const id = crypto.randomUUID();
  try {
    sessionStorage.setItem(key, id);
  } catch {}
  return id;
}

export type LiveCanvasOptions = {
  url: string;
  companyId: string;
  sessionId: string;
  queryClient: QueryClient;
  open?: (url: string) => ChangeSource;
  /** The stream opened (true) or dropped (false): the canvas query polls meanwhile. */
  onOpenChange?: (open: boolean) => void;
};

const parse = <T>(e: MessageEvent): T | null => {
  try {
    return JSON.parse(String(e.data)) as T;
  } catch {
    return null;
  }
};

/** Opens the channel and wires it to the query cache; returns the disconnect function. */
export function connectLiveCanvas(o: LiveCanvasOptions): () => void {
  const open = o.open ?? ((url) => new EventSource(url, { withCredentials: true }));
  const source = open(o.url);
  const stateKey = canvasKeys.state(o.companyId, o.sessionId);
  const stepsKey = canvasKeys.steps(o.companyId, o.sessionId);
  const focusKey = liveFocusKey(o.companyId, o.sessionId);
  const opsKey = liveOpsKey(o.companyId, o.sessionId);
  let closed = false;
  let readies = 0;
  const refetch = () => {
    if (!closed) void o.queryClient.invalidateQueries({ queryKey: stateKey, exact: true });
  };
  const setOpen = (v: boolean) => {
    if (!closed) o.onOpenChange?.(v);
  };

  source.addEventListener("ready", () => {
    setOpen(true);
    // Every `ready` after the first is a reconnect: the replay covers the gap, but a state
    // re-read is cheap and settles anything older than the replay window.
    if (readies++ > 0) refetch();
  });
  source.addEventListener("run.step", (e) => {
    const step = parse<LiveRunStep>(e);
    if (!step || closed) return;
    o.queryClient.setQueryData<LiveRunStep[]>(stepsKey, (list) => mergeSteps(list ?? [], [step]));
  });
  source.addEventListener("canvas.op", (e) => {
    const frame = parse<LiveCanvasOp>(e);
    if (!frame?.op || closed) return;
    const focus = focusOfFrame(frame);
    if (focus) o.queryClient.setQueryData<LiveFocus | null>(focusKey, focus);
    else
      o.queryClient.setQueryData<LiveCanvasOp[]>(opsKey, (list) =>
        [...(list ?? []).filter((x) => x.id !== frame.id), frame].slice(-DESK_OPS_KEPT),
      );
    const cursor = e.lastEventId || undefined;
    o.queryClient.setQueryData<CanvasState>(stateKey, (s) =>
      s ? applyFrame(s, frame, cursor) : s,
    );
    // No state cached yet (the stream beat the first read): read it.
    if (!o.queryClient.getQueryData(stateKey)) refetch();
  });
  const ping = (e: MessageEvent) => {
    const p = parse<ChangePing>(e);
    if (!p || closed) return;
    for (const queryKey of keysForPings(o.companyId, [p]))
      void o.queryClient.invalidateQueries({ queryKey });
  };
  source.addEventListener("task.changed", ping);
  source.addEventListener("helper.commitment", ping);
  source.addEventListener("resync", refetch);
  source.addEventListener("error", () => {
    // EventSource reconnects by itself; the canvas polls until the next `ready`.
    setOpen(false);
    refetch();
  });
  source.addEventListener("end", (e) => {
    const reason = parse<{ reason?: string }>(e)?.reason ?? "";
    setOpen(false);
    if (reason === "revoked" || reason === "unauthenticated") {
      source.close();
      if (!closed) void o.queryClient.invalidateQueries({ queryKey: ["me"] });
    }
  });

  return () => {
    closed = true;
    source.close();
  };
}

export type LiveCanvas = {
  sessionId: string;
  state: UseQueryResult<CanvasState>;
  steps: LiveRunStep[];
  streaming: boolean;
  /** CANVAS-BLOCKS: the latest `focus_view` the agent sent (null: none yet). */
  focus: LiveFocus | null;
  /** LIVE-DESKTOP: the latest canvas ops (not focus_view), oldest first. */
  ops: LiveCanvasOp[];
};

/**
 * The Live canvas of this tab's session: the state query (polled while the stream is down; off
 * against the fake, which has no canvas: the caller falls back), the steps strip and the channel.
 */
export function useLiveCanvas(
  api: Api,
  companyId: string,
  open?: (url: string) => ChangeSource,
): LiveCanvas {
  const queryClient = useQueryClient();
  const [sessionId] = useState(() => liveSessionId(companyId));
  const [streaming, setStreaming] = useState(false);
  const baseUrl = api.mode === "http" ? (api.baseUrl ?? "") : undefined;
  const state = useQuery<CanvasState>({
    queryKey: canvasKeys.state(companyId, sessionId),
    queryFn: () =>
      api.client.liveCanvas.get({
        params: { co: companyId },
        query: { session_id: sessionId },
      }) as Promise<CanvasState>,
    enabled: baseUrl !== undefined || Boolean(open),
    retry: false,
    // 404 is the switch off: the page falls back without a toast.
    meta: { quietStatuses: [403, 404] },
    // A first read that failed (404: the switch is off) stops; a canvas once read keeps polling.
    refetchInterval: (q) => (q.state.data === undefined || streaming ? false : CANVAS_POLL_MS),
  });
  const steps = useQuery<LiveRunStep[]>({
    queryKey: canvasKeys.steps(companyId, sessionId),
    queryFn: () => [],
    staleTime: Number.POSITIVE_INFINITY,
    enabled: false,
    initialData: [],
  });
  const focus = useQuery<LiveFocus | null>({
    queryKey: liveFocusKey(companyId, sessionId),
    queryFn: () => null,
    staleTime: Number.POSITIVE_INFINITY,
    enabled: false,
    initialData: null,
  });
  const ops = useQuery<LiveCanvasOp[]>({
    queryKey: liveOpsKey(companyId, sessionId),
    queryFn: () => [],
    staleTime: Number.POSITIVE_INFINITY,
    enabled: false,
    initialData: [],
  });
  // The stream resumes from the first state's cursor; later reconnects send Last-Event-ID.
  const cursor = useRef<string | undefined>(undefined);
  if (state.data && cursor.current === undefined) cursor.current = state.data.cursor;
  const ready = state.data !== undefined;
  useEffect(() => {
    if (!ready || (baseUrl === undefined && !open)) return;
    if (!open && typeof EventSource === "undefined") return;
    // `/` or unset: the same origin.
    const url = liveCanvasEventsUrl(baseUrl ?? "", companyId, sessionId, cursor.current);
    return connectLiveCanvas({
      url,
      companyId,
      sessionId,
      queryClient,
      onOpenChange: setStreaming,
      ...(open ? { open } : {}),
    });
  }, [ready, baseUrl, companyId, sessionId, queryClient, open]);
  return {
    sessionId,
    state,
    steps: steps.data ?? [],
    streaming,
    focus: focus.data ?? null,
    ops: ops.data ?? [],
  };
}
